import type { OnlineClinic, OnlineMe, OnlinePlan, PaymentSession } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, errorMessage, get, post } from "../api";
import { AgreementText } from "../components/Agreement";
import { StripeCheckout } from "../components/StripeCheckout";
import { Alert, Button, Checkbox, Field, Loading } from "../components/ui";
import { CodeStep, DobNeeded, Frame, NewPersonStep, PhoneStep, SignOut, useLobbyNoAutofill } from "./Book";

type Step = "phone" | "code" | "who" | "new" | "sign" | "pay" | "done";

/** A membership or package's sign-up page: /join/<clinic>/<plan>. Same phone sign-in as booking. */
export function JoinPage() {
  useLobbyNoAutofill();
  const { slug = "", planId = "" } = useParams();
  const base = `/api/book/${slug}`;
  const qc = useQueryClient();
  const page = useQuery({ queryKey: ["book", slug], queryFn: () => get<OnlineClinic>(base), retry: false });
  const plan = useQuery({ queryKey: ["join-plan", slug, planId], queryFn: () => get<OnlinePlan>(`${base}/plans/${planId}`), retry: false });
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [personId, setPersonId] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [started, setStarted] = useState<{ clientPlanId: string; session: PaymentSession } | null>(null);
  const me = page.data?.me ?? null;
  const setMe = (m: OnlineMe | null) => qc.setQueryData<OnlineClinic>(["book", slug], (cur) => (cur ? { ...cur, me: m } : cur));
  const adults = me?.people.filter((p) => !p.minor) ?? [];

  useEffect(() => {
    if (step === "phone" && me) setStep(adults.length ? "who" : "new");
  }, [me, step, adults.length]);

  const join = useMutation({
    mutationFn: () => post<{ clientPlanId: string; session: PaymentSession }>(`${base}/plans/${planId}/join`, { clientId: personId, signedName: signedName.trim(), agreed }),
    onSuccess: (r) => (setStarted(r), setStep("pay")),
    onError: (err) => {
      if (err instanceof ApiError && err.code === "client_signed_out") (setMe(null), setStep("phone"));
    },
  });

  if (page.isLoading || plan.isLoading) return <Frame><Loading /></Frame>;
  if (!page.data) return <Frame><Alert>{errorMessage(page.error)}</Alert></Frame>;
  if (!plan.data) return <Frame clinic={page.data}><Alert>{plan.error instanceof ApiError && plan.error.status === 404 ? "We couldn't find that membership or package." : errorMessage(plan.error)}</Alert></Frame>;
  const p = plan.data;
  const person = adults.find((x) => x.id === personId) ?? null;

  return (
    <Frame clinic={page.data}>
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{p.name}</h1>
        <p className="text-lg font-semibold tabular-nums">
          {formatCents(p.priceCents)}
          {p.kind === "membership" && <span className="text-base font-normal text-ink-soft"> per month</span>}
        </p>
        {p.description && <p className="text-ink-soft">{p.description}</p>}
        <ul className="text-sm text-ink-soft">
          {p.benefits.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        {p.kind === "membership" && (
          <p className="text-xs text-muted">
            {p.minimumMonths > 0 ? `${p.minimumMonths}-month minimum. ` : ""}Billed monthly on your sign-up date. For visits at the clinic.
          </p>
        )}
      </header>
      {!p.available ? (
        <Alert>This can't be bought online right now. Please call us.</Alert>
      ) : step === "phone" ? (
        <PhoneStep base={base} phone={phone} setPhone={setPhone} onSent={() => setStep("code")} clinic={page.data} context="clinic" intro="We'll text you a code to sign in." />
      ) : step === "code" ? (
        <CodeStep
          base={base}
          phone={phone}
          onVerified={(m) => {
            setMe(m);
            setStep(m.people.some((x) => !x.minor) ? "who" : "new");
          }}
          onResend={() => setStep("phone")}
        />
      ) : step === "new" ? (
        <NewPersonStep
          base={base}
          onAdded={(m, name) => {
            setMe(m);
            const added = m.people.find((x) => x.firstName === name.first && x.lastName === name.last);
            if (added) (setPersonId(added.id), setStep("sign"));
            else setStep("who");
          }}
          onError={(err) => err instanceof ApiError && err.code === "client_signed_out" && (setMe(null), setStep("phone"))}
        />
      ) : step === "who" ? (
        <div className="flex flex-col gap-3">
          <h2 className="font-semibold">Who is it for?</h2>
          <DobNeeded base={base} people={adults} onSaved={setMe} />
          {adults.map((x) => (
            <button key={x.id} disabled={x.needsDob} className="rounded-[var(--radius-control)] border border-line bg-white p-4 text-left font-semibold hover:border-brand disabled:opacity-60" onClick={() => (setPersonId(x.id), setStep("sign"))}>
              {x.firstName} {x.lastName}
            </button>
          ))}
          <button className="self-start text-sm font-semibold text-brand" onClick={() => setStep("new")}>
            Someone else
          </button>
          <SignOut base={base} onDone={() => (setMe(null), setStep("phone"))} />
        </div>
      ) : step === "sign" && person ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            join.mutate();
          }}
        >
          {p.agreementText && (
            <>
              <h2 className="font-semibold">Client agreement</h2>
              <div className="max-h-80 overflow-y-auto rounded-[var(--radius-control)] border border-line bg-white p-4">
                <AgreementText text={p.agreementText} />
              </div>
              <Checkbox label="I've read and agree to this agreement" checked={agreed} onChange={setAgreed} />
              <Field label="Type your full name to sign" autoComplete="name" value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder={`${person.firstName} ${person.lastName}`} />
            </>
          )}
          {join.error && <Alert>{errorMessage(join.error)}</Alert>}
          <Button variant="primary" type="submit" className="py-3" busy={join.isPending} disabled={!!p.agreementText && (!agreed || signedName.trim().length < 2)}>
            Continue to payment
          </Button>
          <button type="button" className="self-start text-sm text-muted underline" onClick={() => setStep("who")}>
            ← Not {person.firstName}?
          </button>
        </form>
      ) : step === "pay" && started ? (
        <PayAndWait base={base} started={started} kind={p.kind} onDone={() => setStep("done")} />
      ) : step === "done" ? (
        <div className="flex flex-col gap-4">
          <Alert tone="ok">
            You're all set{person ? `, ${person.firstName}` : ""}! {p.name} is active. {p.kind === "membership" ? "Your sessions apply automatically when you book." : "Your prepaid visits apply automatically when you book."}
          </Alert>
          <Link
            to={`/book/${slug}?${new URLSearchParams({ ...(personId ? { for: personId } : {}), ...(p.voucherServiceIds[0] ? { service: p.voucherServiceIds[0] } : {}) }).toString()}`}
            className="rounded-[var(--radius-control)] bg-brand px-4 py-3 text-center font-semibold text-white hover:bg-brand-deep"
          >
            Book your first visit
          </Link>
          <p className="text-center text-sm text-muted">Or book any time from our booking page.</p>
        </div>
      ) : (
        <Loading />
      )}
    </Frame>
  );
}

function PayAndWait({ base, started, kind, onDone }: { base: string; started: { clientPlanId: string; session: PaymentSession }; kind: OnlinePlan["kind"]; onDone: () => void }) {
  const [finished, setFinished] = useState(false);
  const status = useQuery({
    queryKey: ["join-status", started.clientPlanId],
    queryFn: () => get<{ status: string }>(`${base}/client-plans/${started.clientPlanId}/status`),
    enabled: finished,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "pending" ? false : 1500),
  });
  useEffect(() => {
    if (status.data && status.data.status !== "pending") onDone();
  }, [status.data, onDone]);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-soft">{kind === "membership" ? "Your card is saved for your monthly payment." : "Pay once; your card is saved for your visits."}</p>
      {finished ? (
        <div className="flex items-center gap-3 text-ink-soft">
          <span className="size-4 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden /> Finishing up…
        </div>
      ) : (
        <StripeCheckout session={started.session} onComplete={() => setFinished(true)} />
      )}
    </div>
  );
}
